import type { Connection, Edge } from '@xyflow/react';
import type { Port, ProcessEdge, ProcessNode } from './graphTypes';

/**
 * React Flow's `isValidConnection<E>` callback is invoked with either a
 * fresh `Connection` (when the user drags out a new wire) OR an existing
 * `Edge` (when reconnecting an endpoint). Both shapes carry the four
 * fields we care about — `source`, `target`, `sourceHandle`, `targetHandle`
 * — so we accept the union and normalise on the way in.
 */
type ConnectionLike = Connection | Edge;

const findPort = (
  node: ProcessNode | undefined,
  portId: string | null | undefined,
): Port | undefined => {
  if (!node || !portId) return undefined;
  const ports = (node.data as { ports?: Port[] }).ports;
  if (!Array.isArray(ports)) return undefined;
  return ports.find((p) => p.portId === portId);
};

/**
 * Returns `true` when the (node, handle) pair resolves to a port whose
 * direction does not match the expected one. When the caller passes no
 * node (e.g. unit tests that hand `[]` for nodes) or no handle id, the
 * check is skipped — the dedupe / self-loop guards still apply.
 */
const hasWrongDirection = (
  node: ProcessNode | undefined,
  handleId: string | null,
  expected: 'input' | 'output',
): boolean => {
  if (!handleId || !node) return false;
  return findPort(node, handleId)?.direction !== expected;
};

/**
 * Pure predicate driving `<ReactFlow isValidConnection>`. Returning `false`
 * makes the connection-line preview turn red (or whatever the canvas styles
 * it) and prevents the drop, so the user can't even *attempt* a bad edge.
 *
 * Rules enforced here:
 *   • No self-loops — a node may not connect to itself.
 *   • No duplicates — same {source, sourceHandle, target, targetHandle}
 *     tuple may not appear twice.
 *   • Direction safety — the source handle must resolve to an `output` port
 *     on its node; the target handle must resolve to an `input` port. The
 *     wire format makes this lookup O(1) — no magic strings.
 *
 * Type compatibility — defined as set-overlap between the source's
 * emitted kinds and the target's accepted kinds (a multi-type input
 * matches when ANY of its accepted kinds equals the source's kind) — is
 * intentionally NOT enforced at drag time. The drag-time UX is
 * permissive on purpose: users lay out a graph quickly, and backend
 * validation surfaces any mismatch via the debounced POST /validate
 * after the drop, styling the offending edge in the validation panel.
 * The validation panel is the canonical compatibility signal, not the
 * drag preview.
 *
 * `useProcessGraph.connect` already deduplicates on accept; this predicate
 * is the *prevention* layer that fires before the drop, so the user gets
 * clearer feedback.
 */
export function isValidConnection(
  connection: ConnectionLike,
  edges: readonly ProcessEdge[],
  nodes: readonly ProcessNode[] = [],
): boolean {
  const { source, target } = connection;
  // `Connection` types these as `string | null`; `Edge` types them as
  // `string | null | undefined` — collapse both to a single nullable.
  const sourceHandle = connection.sourceHandle ?? null;
  const targetHandle = connection.targetHandle ?? null;

  if (!source || !target) return false;
  if (source === target) return false;

  if (sourceHandle || targetHandle) {
    const sourceNode = nodes.find((n) => n.id === source);
    const targetNode = nodes.find((n) => n.id === target);
    if (hasWrongDirection(sourceNode, sourceHandle, 'output')) return false;
    if (hasWrongDirection(targetNode, targetHandle, 'input')) return false;
  }

  const isDuplicate = edges.some(
    (e) =>
      e.source === source &&
      e.target === target &&
      (e.sourceHandle ?? null) === sourceHandle &&
      (e.targetHandle ?? null) === targetHandle,
  );
  return !isDuplicate;
}
